import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { pickDetail } from '../lib/data'
import { useAuth } from '../hooks/useAuth'

const TIER_STYLE = {
  'Critical': { bg: '#FCEBEB', color: '#A32D2D', border: '#f09595', dot: '🔴' },
  'High':     { bg: '#FAEEDA', color: '#854F0B', border: '#EF9F27', dot: '🟡' },
  'Medium':   { bg: '#EAF3DE', color: '#3B6D11', border: '#97C459', dot: '🟢' },
  'Low':      { bg: '#F1EFE8', color: '#5F5E5A', border: '#D3D1C7', dot: '⚪' },
}

export default function Dashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [siteRows, setSiteRows] = useState([])
  const [categoryStats, setCategoryStats] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [filterTier, setFilterTier] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [sortBy, setSortBy] = useState('priority')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (profile?.role !== 'admin') { navigate('/'); return }
    fetchDashboard()
  }, [profile])

  async function fetchDashboard() {
    const [sitesRes, auditsRes, answersRes, categoriesRes, questionsRes] = await Promise.all([
      supabase.from('sites').select('id, name, city, state, site_details(priority_tier, priority_score, rank, lpr_issue, gate_issue, district, site_code)').eq('active', true),
      supabase.from('audits').select('id, site_id, status, created_at, auditor_id, profiles(full_name)').order('created_at', { ascending: false }),
      supabase.from('audit_answers').select('audit_id, question_id, result'),
      supabase.from('categories').select('*').eq('active', true).order('sort_order'),
      supabase.from('questions').select('id, category_id').eq('active', true),
    ])

    const sites = sitesRes.data || []
    const audits = auditsRes.data || []
    const answers = answersRes.data || []
    const categories = categoriesRes.data || []
    const questions = questionsRes.data || []

    const totalQ = questions.length

    // Index answers by audit_id
    const answersByAudit = {}
    for (const a of answers) {
      if (!answersByAudit[a.audit_id]) answersByAudit[a.audit_id] = []
      answersByAudit[a.audit_id].push(a)
    }

    // Index audits by site_id (most recent first)
    const auditsBySite = {}
    for (const a of audits) {
      if (!auditsBySite[a.site_id]) auditsBySite[a.site_id] = []
      auditsBySite[a.site_id].push(a)
    }

    // Build per-site rows
    const rows = sites.map(site => {
      const det = pickDetail(site.site_details) || {}
      const siteAudits = auditsBySite[site.id] || []
      const latestAudit = siteAudits[0] || null
      const submittedAudit = siteAudits.find(a => a.status === 'submitted') || null
      const primaryAudit = submittedAudit || latestAudit

      let pass = 0, fail = 0, na = 0, pct = 0
      if (primaryAudit) {
        const ans = answersByAudit[primaryAudit.id] || []
        pass = ans.filter(a => a.result === 'pass').length
        fail = ans.filter(a => a.result === 'fail').length
        na   = ans.filter(a => a.result === 'na').length
        pct  = totalQ ? Math.round((pass + fail + na) / totalQ * 100) : 0
      }

      return {
        id: site.id,
        name: site.name,
        city: site.city,
        state: site.state,
        tier: det.priority_tier,
        rank: det.rank ?? 9999,
        score: det.priority_score,
        district: det.district,
        siteCode: det.site_code,
        lprIssue: det.lpr_issue,
        gateIssue: det.gate_issue,
        auditCount: siteAudits.length,
        latestAudit,
        primaryAudit,
        auditStatus: primaryAudit?.status || null,
        auditorName: primaryAudit?.profiles?.full_name || null,
        pass, fail, na, pct,
        lastAuditDate: latestAudit?.created_at || null,
      }
    })

    // Global stats
    const totalSites = sites.length
    const auditedSites = rows.filter(r => r.auditCount > 0).length
    const submittedSites = rows.filter(r => r.auditStatus === 'submitted').length
    const totalPass = answers.filter(a => a.result === 'pass').length
    const totalFail = answers.filter(a => a.result === 'fail').length
    const criticalSites = rows.filter(r => r.tier === 'Critical').length
    const criticalAudited = rows.filter(r => r.tier === 'Critical' && r.auditCount > 0).length

    setStats({ totalSites, auditedSites, submittedSites, totalPass, totalFail, criticalSites, criticalAudited })

    // Category fail breakdown across all answers
    const catStats = categories.map(cat => {
      const catQIds = new Set(questions.filter(q => q.category_id === cat.id).map(q => q.id))
      const catAnswers = answers.filter(a => catQIds.has(a.question_id))
      const p = catAnswers.filter(a => a.result === 'pass').length
      const f = catAnswers.filter(a => a.result === 'fail').length
      const n = catAnswers.filter(a => a.result === 'na').length
      const total = catQIds.size * Math.max(auditedSites, 1)
      return { ...cat, pass: p, fail: f, na: n, answered: p+f+n, total: catAnswers.length }
    }).sort((a, b) => b.fail - a.fail)
    setCategoryStats(catStats)

    // Recent activity (last 10 audit events)
    setRecentActivity(audits.slice(0, 10))

    setSiteRows(rows)
    setLoading(false)
  }

  // Filter + sort
  const filtered = siteRows
    .filter(r => {
      const matchTier = filterTier === 'All' || r.tier === filterTier
      const matchStatus = filterStatus === 'All'
        || (filterStatus === 'Audited' && r.auditCount > 0)
        || (filterStatus === 'Not audited' && r.auditCount === 0)
        || (filterStatus === 'Submitted' && r.auditStatus === 'submitted')
        || (filterStatus === 'Has failures' && r.fail > 0)
      const matchSearch = !search
        || r.name.toLowerCase().includes(search.toLowerCase())
        || (r.city || '').toLowerCase().includes(search.toLowerCase())
        || (r.district || '').toLowerCase().includes(search.toLowerCase())
      return matchTier && matchStatus && matchSearch
    })
    .sort((a, b) => {
      if (sortBy === 'priority') return a.rank - b.rank
      if (sortBy === 'failures') return b.fail - a.fail
      if (sortBy === 'completion') return b.pct - a.pct
      if (sortBy === 'recent') return (b.lastAuditDate || '').localeCompare(a.lastAuditDate || '')
      if (sortBy === 'name') return a.name.localeCompare(b.name)
      return 0
    })

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">Cross-site audit overview</div>
        </div>
        <button className="btn btn-sm" onClick={fetchDashboard}>↻ Refresh</button>
      </div>

      {/* Top stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 20 }}>
          <StatCard n={stats.totalSites}      label="Total sites"         />
          <StatCard n={stats.auditedSites}    label="Sites started"       color="var(--amber)" />
          <StatCard n={stats.submittedSites}  label="Audits submitted"    color="var(--green)" />
          <StatCard n={`${stats.criticalAudited}/${stats.criticalSites}`} label="Critical audited" color="#A32D2D" />
          <StatCard n={stats.totalPass}       label="Total passes"        color="var(--green)" />
          <StatCard n={stats.totalFail}       label="Total failures"      color="var(--red)" />
        </div>
      )}

      {/* Category failure breakdown */}
      {categoryStats.some(c => c.fail > 0 || c.pass > 0) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Failures by category</h2>
          {categoryStats.map(cat => {
            const total = Math.max(cat.answered, 1)
            const failPct = Math.round(cat.fail / total * 100)
            const passPct = Math.round(cat.pass / total * 100)
            return (
              <div key={cat.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span>{cat.icon} {cat.name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {cat.fail > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>{cat.fail} fail · </span>}
                    {cat.pass} pass · {cat.answered} answered
                  </span>
                </div>
                <div style={{ height: 8, background: 'var(--gray-light)', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${passPct}%`, background: '#639922', transition: 'width .3s' }} />
                  <div style={{ width: `${failPct}%`, background: '#E24B4A', transition: 'width .3s' }} />
                </div>
              </div>
            )
          })}
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#639922', display: 'inline-block' }} /> Pass</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#E24B4A', display: 'inline-block' }} /> Fail</span>
          </div>
        </div>
      )}

      {/* Site table filters */}
      <div style={{ marginBottom: 12 }}>
        <input
          className="form-input"
          placeholder="Search sites, cities, districts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {['All','Critical','High','Medium','Low'].map(t => {
            const ts = TIER_STYLE[t]
            const active = filterTier === t
            return (
              <button key={t} onClick={() => setFilterTier(t)} style={{
                padding: '4px 10px', borderRadius: 20, border: `1px solid ${active && ts ? ts.color : 'var(--border)'}`,
                background: active && ts ? ts.bg : 'var(--bg)', color: active && ts ? ts.color : 'var(--text-muted)',
                fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer',
              }}>
                {ts?.dot} {t}
              </button>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['All','Not audited','Audited','Submitted','Has failures'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} style={{
                padding: '4px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                border: `1px solid ${filterStatus === s ? 'var(--text)' : 'var(--border)'}`,
                background: filterStatus === s ? 'var(--text)' : 'var(--bg)',
                color: filterStatus === s ? 'var(--bg)' : 'var(--text-muted)',
                fontWeight: filterStatus === s ? 600 : 400,
              }}>
                {s}
              </button>
            ))}
          </div>
          <select
            className="form-input"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ width: 'auto', fontSize: 12, padding: '4px 8px' }}
          >
            <option value="priority">Sort: Priority</option>
            <option value="failures">Sort: Most failures</option>
            <option value="completion">Sort: Completion %</option>
            <option value="recent">Sort: Recently audited</option>
            <option value="name">Sort: Name</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        Showing {filtered.length} of {siteRows.length} sites
      </div>

      {/* Site rows */}
      {filtered.map(row => {
        const ts = TIER_STYLE[row.tier]
        return (
          <div
            key={row.id}
            className="card"
            style={{ marginBottom: 8, cursor: 'pointer', transition: 'border-color .15s' }}
            onClick={() => navigate('/reports', { state: { siteId: row.id } })}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              {/* Left */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</span>
                  {row.tier && (
                    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: ts?.bg, color: ts?.color, fontWeight: 500, flexShrink: 0 }}>
                      {ts?.dot} {row.tier}
                    </span>
                  )}
                  {row.fail > 0 && (
                    <span className="badge badge-fail" style={{ fontSize: 11 }}>{row.fail} fail</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {[row.city, row.state].filter(Boolean).join(', ')}
                  {row.district ? ` · ${row.district}` : ''}
                  {row.lprIssue ? ' · ⚠️ LPR' : ''}
                  {row.gateIssue ? ' · ⚠️ Gate' : ''}
                </div>
              </div>

              {/* Right */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                {row.auditCount === 0 ? (
                  <span className="badge badge-na">Not audited</span>
                ) : (
                  <>
                    <span className={`badge ${row.auditStatus === 'submitted' ? 'badge-pass' : 'badge-warn'}`}>
                      {row.auditStatus === 'submitted' ? 'Submitted' : 'In progress'}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 80, height: 5, background: 'var(--gray-light)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${row.pct}%`, height: '100%', background: row.fail > 0 ? '#E24B4A' : '#639922', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 30 }}>{row.pct}%</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Audit meta row */}
            {row.lastAuditDate && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
                <span>Auditor: {row.auditorName || '—'}</span>
                <span>{new Date(row.lastAuditDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        )
      })}

      {filtered.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <h3>No sites match</h3>
          <p>Try adjusting the filters or search.</p>
        </div>
      )}

      {/* Recent activity */}
      {recentActivity.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Recent activity</h2>
          {recentActivity.map((audit, i) => {
            const siteRow = siteRows.find(s => s.id === audit.site_id)
            return (
              <div key={audit.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: i < recentActivity.length - 1 ? '1px solid var(--border)' : 'none',
                flexWrap: 'wrap', gap: 6,
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{siteRow?.name || 'Unknown site'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {audit.profiles?.full_name || 'Unknown auditor'} · {new Date(audit.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
                <span className={`badge ${audit.status === 'submitted' ? 'badge-pass' : 'badge-warn'}`}>
                  {audit.status === 'submitted' ? 'Submitted' : 'In progress'}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function StatCard({ n, label, color }) {
  return (
    <div className="stat-card">
      <div className="stat-number" style={{ color: color || 'var(--text)' }}>{n}</div>
      <div className="stat-label">{label}</div>
    </div>
  )
}
